import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import axios from '../utils/api';
import '../styles/Settings.css';
import '../styles/Support.css';

const MAX_TICKET_FILE_SIZE = 25 * 1024 * 1024;
const MAX_TICKET_FILES = 5;
const UPLOAD_TIMEOUT_MS = 120000;
const formatFileMb = (bytes) => `${(Number(bytes || 0) / 1024 / 1024).toFixed(1)} MB`;
const apiError = (err, fallback) =>
  err.response?.data?.error || err.response?.data?.detail || fallback;

const TicketAttachments = ({ items }) => {
  if (!items?.length) return null;
  return (
    <ul className="ticket-attachments">
      {items.map((file, index) => (
        <li key={`${file.url || file.name}-${index}`}>
          <a href={file.url} target="_blank" rel="noopener noreferrer">
            <i className="fas fa-paperclip"></i>
            {file.name || 'Attachment'}
            {file.size ? ` (${formatFileMb(file.size)})` : ''}
          </a>
        </li>
      ))}
    </ul>
  );
};

const SupportTickets = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [reply, setReply] = useState('');
  const [newFiles, setNewFiles] = useState([]);
  const [replyFiles, setReplyFiles] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/auth/support/tickets/');
      setTickets(res.data?.tickets || []);
    } catch (err) {
      setError(apiError(err, 'Support is not available yet.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTicket = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`/auth/support/tickets/${id}/`);
      setTicket(res.data?.ticket || null);
    } catch (err) {
      setTicket(null);
      setError(apiError(err, 'Could not load ticket.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    document.title = id ? 'Support ticket' : 'Support';
    if (id) loadTicket();
    else loadList();
  }, [id, loadTicket, loadList]);

  const collectFiles = (incoming, current) => {
    const next = [...current];
    const rejected = [];
    incoming.forEach((file) => {
      if (next.length >= MAX_TICKET_FILES) {
        rejected.push(`${file.name} — max ${MAX_TICKET_FILES} files`);
        return;
      }
      if (file.size > MAX_TICKET_FILE_SIZE) {
        rejected.push(`${file.name} (${formatFileMb(file.size)}) — max ${formatFileMb(MAX_TICKET_FILE_SIZE)}`);
        return;
      }
      next.push(file);
    });
    if (rejected.length) {
      alert(`These files were not attached:\n\n${rejected.join('\n')}`);
    }
    return next;
  };

  const createTicket = async (e) => {
    e.preventDefault();
    if (!subject.trim() || (!body.trim() && !newFiles.length)) {
      setError('Subject and a message or attachment are required.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('subject', subject.trim());
      formData.append('body', body.trim());
      newFiles.forEach((file) => formData.append('attachments', file));
      const res = await axios.post('/auth/support/tickets/', formData, { timeout: UPLOAD_TIMEOUT_MS });
      const created = res.data?.ticket;
      setSubject('');
      setBody('');
      setNewFiles([]);
      if (created?.id) navigate(`/dashboard/support/${created.id}`);
      else loadList();
    } catch (err) {
      setError(apiError(err, 'Could not open ticket.'));
    } finally {
      setSending(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() && !replyFiles.length) return;
    setSending(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('body', reply.trim());
      replyFiles.forEach((file) => formData.append('attachments', file));
      const res = await axios.post(`/auth/support/tickets/${id}/`, formData, { timeout: UPLOAD_TIMEOUT_MS });
      setTicket(res.data?.ticket || null);
      setReply('');
      setReplyFiles([]);
    } catch (err) {
      setError(apiError(err, 'Could not send reply.'));
    } finally {
      setSending(false);
    }
  };

  if (id) {
    return (
      <div className="settings-page">
        <button type="button" className="merchant-back" onClick={() => navigate('/dashboard/support')}>
          ← All tickets
        </button>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading || !ticket ? (
          <p>{loading ? 'Loading…' : 'Ticket not found.'}</p>
        ) : (
          <>
            <div className="page-header">
              <h1>{ticket.subject}</h1>
              <p className="page-description">{ticket.status.replace('_', ' ')}</p>
            </div>
            <div className="settings-section ticket-thread">
              {(ticket.messages || []).map((msg) => (
                <div key={msg.id} className={`ticket-bubble ticket-bubble-${msg.sender}`}>
                  <div className="ticket-bubble-meta">
                    <strong>{msg.sender === 'support' ? 'JEIC support' : (msg.author_name || 'You')}</strong>
                    <span>{msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}</span>
                  </div>
                  <p>{msg.body}</p>
                  <TicketAttachments items={msg.attachments} />
                </div>
              ))}
              {ticket.status !== 'closed' && (
                <form onSubmit={sendReply} className="settings-form">
                  <div className="form-group">
                    <label htmlFor="reply">Reply</label>
                    <textarea id="reply" className="form-control" rows={4} value={reply} onChange={(e) => setReply(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="reply-files">Attachments</label>
                    <input
                      id="reply-files"
                      type="file"
                      multiple
                      className="form-control"
                      onChange={(e) => {
                        setReplyFiles(collectFiles(Array.from(e.target.files || []), replyFiles));
                        e.target.value = '';
                      }}
                    />
                    <small style={{ color: '#64748b' }}>
                      Optional. Up to {MAX_TICKET_FILES} files, {formatFileMb(MAX_TICKET_FILE_SIZE)} each.
                    </small>
                    {replyFiles.length > 0 && (
                      <ul className="ticket-attachments ticket-attachments-pending">
                        {replyFiles.map((file, index) => (
                          <li key={`${file.name}-${index}`}>
                            <span>{file.name} ({formatFileMb(file.size)})</span>
                            <button type="button" className="ticket-file-remove" onClick={() => setReplyFiles((prev) => prev.filter((_, i) => i !== index))}>
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={sending || (!reply.trim() && !replyFiles.length)}>
                    {sending ? 'Sending…' : 'Send reply'}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Support</h1>
        <p className="page-description">Raise a ticket with JEIC. Replies stay in this conversation.</p>
      </div>
      {error && <Alert variant="warning">{error}</Alert>}

      <div className="settings-section">
        <div className="section-header">
          <i className="fas fa-plus"></i>
          <h2>New ticket</h2>
        </div>
        <form onSubmit={createTicket} className="settings-form">
          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input id="subject" className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} required />
          </div>
          <div className="form-group">
            <label htmlFor="body">Message</label>
            <textarea id="body" className="form-control" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="new-files">Attachments</label>
            <input
              id="new-files"
              type="file"
              multiple
              className="form-control"
              onChange={(e) => {
                setNewFiles(collectFiles(Array.from(e.target.files || []), newFiles));
                e.target.value = '';
              }}
            />
            <small style={{ color: '#64748b' }}>
              Optional. Up to {MAX_TICKET_FILES} files, {formatFileMb(MAX_TICKET_FILE_SIZE)} each.
            </small>
            {newFiles.length > 0 && (
              <ul className="ticket-attachments ticket-attachments-pending">
                {newFiles.map((file, index) => (
                  <li key={`${file.name}-${index}`}>
                    <span>{file.name} ({formatFileMb(file.size)})</span>
                    <button type="button" className="ticket-file-remove" onClick={() => setNewFiles((prev) => prev.filter((_, i) => i !== index))}>
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button type="submit" className="btn btn-primary" disabled={sending || !subject.trim() || (!body.trim() && !newFiles.length)}>
            {sending ? 'Opening…' : 'Open ticket'}
          </button>
        </form>
      </div>

      <div className="settings-section">
        <div className="section-header">
          <i className="fas fa-comments"></i>
          <h2>Your tickets</h2>
        </div>
        {loading ? (
          <p>Loading…</p>
        ) : !tickets.length ? (
          <p>No tickets yet.</p>
        ) : (
          <ul className="notice-list">
            {tickets.map((item) => (
              <li key={item.id} className="ticket-row" onClick={() => navigate(`/dashboard/support/${item.id}`)}>
                <strong>{item.subject}</strong>
                <p>{item.status.replace('_', ' ')} · {item.updated_at ? new Date(item.updated_at).toLocaleString() : ''}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SupportTickets;
